// File Name: AboutPage.jsx

import React from "react";
import { motion } from "framer-motion";

import Stats from "../components/Stats";
import WhyChoose from "../components/WhyChoose";
import Particles from "../components/Particles";

/* MEMBER IMAGE */
import memberImage from "../assets/member.jpg";

import {
  Target,
  Eye,
  ShieldCheck,
  Code,
  Boxes,
  Lock,
  ArrowRight,
  Linkedin,
  Mail,
} from "lucide-react";

const EXPERTISE = [
  {
    icon: Code,
    title: "Software Development",
    desc:
      "Modern scalable applications engineered with high performance, security and future-ready architecture.",
  },
  {
    icon: Boxes,
    title: "Scrum Management",
    desc:
      "Agile delivery models that ensure predictable execution, collaboration and faster project delivery.",
  },
  {
    icon: ShieldCheck,
    title: "Cyber Security Architecture",
    desc:
      "Advanced security strategies designed to protect businesses against evolving cyber threats.",
  },
  {
    icon: Lock,
    title: "Information Security Architecture",
    desc:
      "Secure governance, compliance and enterprise-level protection for digital infrastructure.",
  },
];

/* TEAM MEMBERS */

const MEMBERS = [
  {
    name: "James Anderson",
    role: "President and CEO",
    image: memberImage,
  },
  {
    name: "Ibtehaj Syed",
    role: "Director and HR Head",
    image: memberImage,
  },
  {
    name: "Daniel Foster",
    role: "CMO",
    image: memberImage,
  },
  {
    name: "Abraham J. Mitchel",
    role: "CFO",
    image: memberImage,
  },
];

export default function AboutPage() {
  return (
    <>
      {/* GLOBAL STYLES */}
      <style>
        {`
          .about-bg {
            background:
              radial-gradient(circle at top right, rgba(124,58,237,0.16), transparent 35%),
              radial-gradient(circle at bottom left, rgba(168,85,247,0.12), transparent 40%),
              linear-gradient(to bottom, #ffffff, #faf7ff);
          }

          .glass-card {
            background: rgba(255,255,255,0.86);
            border: 1px solid rgba(124,58,237,0.10);
            backdrop-filter: blur(18px);
            box-shadow: 0 12px 40px rgba(124,58,237,0.10);
            transition: all 0.4s ease;
          }

          .glass-card:hover {
            transform: translateY(-8px);
            box-shadow: 0 24px 60px rgba(124,58,237,0.18);
          }

          .gradient-button {
            background: linear-gradient(135deg, #7C3AED, #A855F7);
            transition: all 0.35s ease;
            box-shadow: 0 14px 35px rgba(124,58,237,0.25);
          }

          .gradient-button:hover {
            transform: translateY(-4px);
            box-shadow: 0 22px 50px rgba(124,58,237,0.32);
          }

          .expertise-card {
            background: rgba(255,255,255,0.96);
            border: 1px solid rgba(124,58,237,0.08);
            transition: all 0.35s ease;
            box-shadow: 0 12px 30px rgba(15,23,42,0.04);
            position: relative;
            overflow: hidden;
          }

          .expertise-card:hover {
            transform: translateY(-10px);
            box-shadow: 0 24px 60px rgba(124,58,237,0.16);
            border-color: rgba(124,58,237,0.18);
          }

          .expertise-card::before {
            content: "";

            position: absolute;

            top: -40px;
            right: -40px;

            width: 140px;
            height: 140px;

            border-radius: 999px;

            background: rgba(168,85,247,0.12);

            filter: blur(50px);

            opacity: 0;

            transition: opacity 0.4s ease;
          }

          .expertise-card:hover::before {
            opacity: 1;
          }

          .team-card {
            background: rgba(255,255,255,0.94);
            border: 1px solid rgba(124,58,237,0.10);
            backdrop-filter: blur(18px);
            transition: all 0.35s ease;
            overflow: hidden;
          }

          .team-card:hover {
            transform: translateY(-10px);
            box-shadow: 0 28px 70px rgba(124,58,237,0.18);
          }

          .purple-badge {
            background: rgba(124,58,237,0.08);
            border: 1px solid rgba(124,58,237,0.14);
          }

          .purple-glow {
            position: absolute;
            width: 500px;
            height: 500px;
            border-radius: 999px;
            background: rgba(124,58,237,0.12);
            filter: blur(120px);
            pointer-events: none;
          }

          /* ========================= */
          /* MOBILE ANIMATIONS */
          /* ========================= */

          @keyframes floatMobile {
            0% {
              transform: translateY(0px);
            }

            50% {
              transform: translateY(-10px);
            }

            100% {
              transform: translateY(0px);
            }
          }

          @keyframes pulseGlow {
            0% {
              opacity: 0.4;
              transform: scale(1);
            }

            50% {
              opacity: 0.8;
              transform: scale(1.08);
            }

            100% {
              opacity: 0.4;
              transform: scale(1);
            }
          }

          @keyframes mobileShine {
            0% {
              transform: translateX(-120%);
            }

            100% {
              transform: translateX(220%);
            }
          }

          /* ========================= */
          /* MOBILE RESPONSIVE */
          /* ========================= */

          @media (max-width: 768px) {

            .purple-glow {
              width: 280px;
              height: 280px;
              filter: blur(90px);

              animation:
                pulseGlow 6s ease-in-out infinite;
            }

            .glass-card,
            .expertise-card,
            .team-card {
              border-radius: 24px;
            }

            .glass-card:hover,
            .expertise-card:hover,
            .team-card:hover {
              transform: none;
            }

            .expertise-card {
              animation:
                floatMobile 5s ease-in-out infinite;
            }

            .expertise-card:nth-child(2) {
              animation-delay: 1s;
            }

            .expertise-card:nth-child(3) {
              animation-delay: 2s;
            }

            .expertise-card:nth-child(4) {
              animation-delay: 3s;
            }

            .team-card {
              animation:
                floatMobile 6s ease-in-out infinite;
            }

            .team-card::after {
              content: "";

              position: absolute;

              top: 0;
              left: -120%;

              width: 60%;
              height: 100%;

              background:
                linear-gradient(
                  90deg,
                  transparent,
                  rgba(255,255,255,0.25),
                  transparent
                );

              transform: skewX(-20deg);

              animation:
                mobileShine 4.5s linear infinite;
            }

            .gradient-button {
              width: 100%;
              justify-content: center;
            }
          }

          @media (max-width: 640px) {

            .about-bg {
              padding-top: 110px;
            }

            h1 {
              font-size: 2.9rem !important;
              line-height: 1.05 !important;
            }

            h2 {
              font-size: 2.3rem !important;
              line-height: 1.1 !important;
            }

            p {
              font-size: 15px !important;
              line-height: 1.75 !important;
            }
          }
        `}
      </style>

      {/* HERO SECTION */}
      <section className="about-bg relative pt-36 pb-28 overflow-hidden">

        <div className="purple-glow top-0 right-0"></div>
        <div className="purple-glow bottom-0 left-0"></div>

        <Particles count={18} />

        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

            {/* LEFT CONTENT */}
            <motion.div
              initial={{ opacity: 0, y: 25 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >

              <div className="inline-flex items-center px-5 py-2 rounded-full purple-badge text-xs uppercase tracking-[0.35em] text-violet-700 font-bold mb-6">
                ABOUT US
              </div>

              <div className="mb-8">

                <div className="text-6xl md:text-7xl font-extrabold text-slate-900 leading-none">
                  10+
                </div>

                <div className="text-sm uppercase tracking-[0.3em] text-slate-500 mt-3">
                  Years of Excellence
                </div>

              </div>

              <h1 className="text-5xl md:text-6xl font-bold text-slate-900 leading-[1.05]">

                A legacy of{" "}

                <span className="bg-gradient-to-r from-violet-700 to-fuchsia-500 bg-clip-text text-transparent">
                  engineered trust
                </span>

              </h1>

              <p className="mt-7 text-lg text-slate-600 leading-relaxed max-w-2xl">
                CodeFix Solutions is a professional IT services company delivering
                scalable software, cybersecurity, consulting, and enterprise solutions
                with over a decade of proven expertise.
              </p>

              <div className="mt-10 flex flex-wrap gap-5">

                <button className="gradient-button inline-flex items-center gap-2 px-8 py-4 rounded-2xl text-white font-semibold">
                  Explore Services
                  <ArrowRight className="w-4 h-4" />
                </button>

                <button className="w-full sm:w-auto px-8 py-4 rounded-2xl border border-violet-100 bg-white text-slate-700 font-semibold hover:border-violet-500 hover:text-violet-700 hover:shadow-xl transition-all duration-300">
                  Contact Us
                </button>

              </div>

            </motion.div>

            {/* RIGHT IMAGE */}
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7 }}
              className="relative"
            >

              <div className="absolute -inset-4 bg-gradient-to-r from-violet-500/20 to-fuchsia-500/20 blur-3xl rounded-[40px]"></div>

              <div className="relative rounded-[34px] overflow-hidden border border-white/40 shadow-[0_35px_90px_rgba(124,58,237,0.20)]">

                <img
                  src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=80&w=1400&auto=format&fit=crop"
                  alt="About"
                  className="
                    w-full
                    h-[520px]
                    md:h-[520px]
                    sm:h-[420px]
                    h-[340px]
                    object-cover
                  "
                />

              </div>

            </motion.div>

          </div>
        </div>
      </section>

      {/* STATS */}
      <Stats />

      {/* MISSION & VISION */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8">

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">

            {[
              {
                icon: Target,
                label: "Our Mission",
                text:
                  "To empower organizations with intelligent technology solutions and secure digital ecosystems.",
              },
              {
                icon: Eye,
                label: "Our Vision",
                text:
                  "To become a globally trusted innovation partner delivering transformative technology solutions.",
              },
            ].map((b) => (
              <motion.div
                key={b.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="glass-card rounded-[32px] p-10"
              >

                <div className="w-16 h-16 rounded-2xl bg-violet-50 flex items-center justify-center mb-7 shadow-sm">
                  <b.icon className="w-8 h-8 text-violet-600" />
                </div>

                <h3 className="text-3xl font-bold mb-5 text-slate-900">
                  {b.label}
                </h3>

                <p className="text-slate-600 text-lg leading-relaxed">
                  {b.text}
                </p>

              </motion.div>
            ))}

          </div>

        </div>
      </section>

      {/* EXPERTISE */}
      <section className="py-24 bg-[#faf7ff]">
        <div className="max-w-7xl mx-auto px-6 md:px-8">

          <div className="text-center max-w-3xl mx-auto mb-16">

            <div className="inline-flex items-center px-5 py-2 rounded-full purple-badge text-sm uppercase tracking-[0.35em] text-violet-700 font-bold mb-6">
              OUR EXPERTISE
            </div>

            <h2 className="text-5xl md:text-6xl font-bold text-slate-900 leading-tight">

              Technology Built For

              <span className="bg-gradient-to-r from-violet-700 to-fuchsia-500 bg-clip-text text-transparent">
                {" "}Modern Enterprises
              </span>

            </h2>

          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-7">

            {EXPERTISE.map((e) => (
              <motion.div
                key={e.title}
                whileHover={{ y: -8 }}
                className="expertise-card rounded-[30px] p-8"
              >

                <div className="w-16 h-16 rounded-2xl bg-violet-50 flex items-center justify-center mb-7 shadow-sm">
                  <e.icon className="w-8 h-8 text-violet-600" />
                </div>

                <h3 className="text-xl font-bold text-slate-900 mb-4">
                  {e.title}
                </h3>

                <p className="text-slate-600 leading-relaxed">
                  {e.desc}
                </p>

              </motion.div>
            ))}

          </div>
        </div>
      </section>

      {/* TEAM SECTION */}
      <section className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8">

          <div className="text-center max-w-3xl mx-auto mb-20">

            <div className="inline-flex items-center px-5 py-2 rounded-full purple-badge text-sm uppercase tracking-[0.35em] text-violet-700 font-bold mb-6">
              OUR LEADERSHIP
            </div>

            <h2 className="text-5xl md:text-6xl font-bold text-slate-900 leading-tight">

              Meet Our

              <span className="bg-gradient-to-r from-violet-700 to-fuchsia-500 bg-clip-text text-transparent">
                {" "}Executive Team
              </span>

            </h2>

            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              Experienced leaders delivering innovation, enterprise technology,
              and cybersecurity excellence.
            </p>

          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">

            {MEMBERS.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 35 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="group"
              >

                <div className="team-card rounded-[32px] overflow-hidden">

                  <div className="relative overflow-hidden">

                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-[340px] object-cover group-hover:scale-105 transition duration-700"
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>

                    {/* SOCIAL ICONS */}
                    <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-3 opacity-0 group-hover:opacity-100 transition duration-500">

                      <button className="w-11 h-11 rounded-full bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center hover:bg-violet-600 transition-all duration-300">
                        <Linkedin className="w-4 h-4 text-white" />
                      </button>

                      <button className="w-11 h-11 rounded-full bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center hover:bg-violet-600 transition-all duration-300">
                        <Mail className="w-4 h-4 text-white" />
                      </button>

                    </div>

                  </div>

                  <div className="p-7 text-center">

                    <h3 className="text-xl font-bold text-slate-900">
                      {member.name}
                    </h3>

                    <p className="mt-2 text-violet-600 font-semibold text-sm tracking-wide uppercase">
                      {member.role}
                    </p>

                  </div>

                </div>

              </motion.div>
            ))}

          </div>

        </div>
      </section>

      <WhyChoose />
    </>
  );
}